'use client';

import { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface CommunityGuidelinesModalProps {
  isOpen: boolean;
  /** Return value is ignored — handlers may be sync or async (e.g. accept() resolves a boolean). */
  onAgree: () => void | Promise<unknown>;
  /**
   * When provided, renders a close affordance (X button + backdrop click).
   * Omit it for a blocking modal the user must accept — see CommunityGuidelinesGate.
   */
  onClose?: () => void;
  saving?: boolean;
  errorMessage?: string | null;
  fullGuidelinesHref?: string;
}

const ERROR_ID = 'community-guidelines-error';
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function CommunityGuidelinesModal({
  isOpen,
  onAgree,
  onClose,
  saving = false,
  errorMessage = null,
  fullGuidelinesHref = '/guidelines-legal?tab=community-guidelines',
}: CommunityGuidelinesModalProps) {
  const { t } = useLanguage();
  const panelRef = useRef<HTMLDivElement>(null);

  // Lock background scroll while the modal is open — it covers the viewport
  // and (in gate mode) blocks app use until accepted.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Focus trap. In gate mode this modal is blocking, so keyboard/AT users must
  // not be able to Tab into the app behind the overlay. Move focus in on open
  // and cycle Tab within the panel.
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const getFocusable = () =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    // Move focus into the modal on open.
    const initial = getFocusable();
    (initial[0] ?? panel).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      const inPanel = active instanceof Node && panel.contains(active);

      if (!inPanel) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const copy = t.modals.communityGuidelines;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-guidelines-title"
      data-testid="community-guidelines-modal-root"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose ? () => onClose() : undefined}
        data-testid="community-guidelines-backdrop"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative bg-background rounded-2xl shadow-xl w-full max-w-4xl mx-auto p-6 sm:p-8 md:p-12 max-h-[90vh] overflow-y-auto outline-none"
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-foreground-muted hover:text-foreground"
            aria-label="Close"
            data-testid="community-guidelines-close-btn"
          >
            <X size={20} />
          </button>
        )}

        <h2
          id="community-guidelines-title"
          className="font-serif text-2xl sm:text-3xl md:text-4xl text-primary mb-3"
        >
          {copy.title}
        </h2>
        <p className="text-base md:text-lg font-semibold text-secondary mb-5">
          {copy.subtitle}
        </p>
        <p className="text-sm md:text-base text-foreground mb-8">{copy.lead}</p>

        {/* Mobile: sections stack with a horizontal rule between them.
            Desktop (md+): two columns split by a vertical rule. */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          <div className="pb-7 md:pb-0 md:pr-8">
            <h3 className="text-base font-semibold text-primary mb-4">
              {copy.allowedHeading}
            </h3>
            <ul className="space-y-3">
              {copy.allowed.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm md:text-base text-foreground">
                  <Check size={20} className="mt-0.5 flex-shrink-0 text-secondary" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-7 md:pt-0 md:pl-8">
            <h3 className="text-base font-semibold text-primary mb-4">
              {copy.notAllowedHeading}
            </h3>
            <ul className="space-y-3">
              {copy.notAllowed.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm md:text-base text-foreground">
                  <X size={20} className="mt-0.5 flex-shrink-0 text-orange-500" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-xs md:text-sm text-foreground-muted mt-8 md:mt-10 mb-6 leading-relaxed">
          {copy.consentText}
        </p>

        {errorMessage && (
          <p
            id={ERROR_ID}
            className="text-sm text-red-600 mb-4"
            role="alert"
            data-testid="community-guidelines-error"
          >
            {errorMessage}
          </p>
        )}

        <button
          type="button"
          onClick={onAgree}
          disabled={saving}
          aria-describedby={errorMessage ? ERROR_ID : undefined}
          className="w-full sm:w-auto px-12 py-3 bg-primary text-white rounded-full text-sm font-semibold tracking-wider uppercase hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="community-guidelines-agree-btn"
        >
          {saving ? copy.saving : copy.agreeBtn}
        </button>

        <div className="mt-4">
          <a
            href={fullGuidelinesHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-secondary hover:underline"
            data-testid="community-guidelines-full-link"
          >
            {copy.viewFullLink} →<span className="sr-only"> ({copy.opensInNewTab})</span>
          </a>
        </div>
      </div>
    </div>
  );
}
